import { FileX } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
// import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
// import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ApplicationFilters from '@/components/DashboardFilters/ApplicationFilters';
import {
   Table,
   TableBody,
   TableHead,
   TableHeader,
   TableRow
} from "@/components/ui/table";
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useTypewriter } from '@/hooks/useTypewriter';
import { gql, useApolloClient, useMutation, useQuery } from '@apollo/client';
import { useApplications } from '../../../hooks/useApplications';
import TS_ApplicationRow from './TS_ApplicationRow';
// import {
//    DropdownMenu,
//    DropdownMenuContent,
//    DropdownMenuItem,
//    DropdownMenuTrigger,
// } from "@/components/ui/dropdown-menu";
import DashboardLayout from '@/components/layouts/DashboardLayout';
import ApplicationTableHeader from '@/components/shared/ApplicationTableHeader';
import ApplicationViewModal from '@/components/sharedModals/applicationViewModal/ApplicationViewModal';
import CertificateViewModal from '@/components/sharedModals/CertificateViewModal';
import HistoryModal from '@/components/sharedModals/HistoryModal';
import ReleaseConfirmationModal from '@/components/sharedModals/ReleaseConfirmationModal';
import useTabNavigation from '@/hooks/useTabNavigation';
import useTourGuide from '@/hooks/useTourGuide';
import { getAdminId, getStaffName, getStaffRole, getUserRoles, isTechnicalStaff } from '@/utils/auth';
import { toast } from 'sonner';
import { useOrderOfPayments } from '../../../hooks/useOrderOfPayments';
import TS_InspectionReportsViewModal from '../../InspectionDashboardComponents/TS_InspectionReportsViewModal';
import PersonnelApplicationCard from './../PersonnelApplicationCard';
import CertificateActionHandler from './CertificateActionHandler';
import ForestRangerSignatureModal from './ForestRangerSignatureModal';
import TechnicalStaffOOPRow from './TechnicalStaffOOPRow';
import { getTSActions } from './TS_ApplicationRow';
import TS_AuthenticityReviewModal from './TS_AuthenticityReviewModal';
import TS_ReviewModal from './TS_ReviewModal';

// Add mutations
const UPDATE_PERMIT_STAGE = gql`
   mutation UpdatePermitStage(
      $id: ID!,
      $currentStage: String!,
      $status: String!,
      $notes: String,
      $approvedByTechnicalStaff: Boolean,
      $awaitingPermitCreation: Boolean,
      $acceptedByTechnicalStaff: Boolean,
      $PermitCreated: Boolean,
      $hasCertificate: Boolean,
      $certificateId: ID,
      $ApprovedInventoryRequest: Boolean,
      $awaitingOOP: Boolean,
      $certificateSignedByForestRanger: Boolean
   ) {
      updatePermitStage(
         id: $id,
         currentStage: $currentStage,
         status: $status,
         notes: $notes,
         approvedByTechnicalStaff: $approvedByTechnicalStaff,
         awaitingPermitCreation: $awaitingPermitCreation,
         acceptedByTechnicalStaff: $acceptedByTechnicalStaff,
         PermitCreated: $PermitCreated,
         hasCertificate: $hasCertificate,
         certificateId: $certificateId,
         ApprovedInventoryRequest: $ApprovedInventoryRequest,
         awaitingOOP: $awaitingOOP,
         certificateSignedByForestRanger: $certificateSignedByForestRanger
      ) {
         id
         currentStage
         status
         acceptedByTechnicalStaff
         awaitingPermitCreation
         approvedByTechnicalStaff
         PermitCreated
         hasCertificate
         certificateId
         ApprovedInventoryRequest
         awaitingOOP
         certificateSignedByForestRanger
      }
   }
`;

const UNDO_ACCEPTANCE_TECHNICAL_STAFF = gql`
   mutation UndoAcceptanceTechnicalStaff(
      $id: ID!
      $currentStage: String!
      $status: String!
      $acceptedByTechnicalStaff: Boolean!
      $approvedByTechnicalStaff: Boolean!
      $assignedTechnicalStaffId: ID
      $assignedTechnicalStaffName: String
      $awaitingOOP: Boolean!
      $awaitingPermitCreation: Boolean!
   ) {
      undoAcceptanceTechnicalStaff(
         id: $id
         currentStage: $currentStage
         status: $status
         acceptedByTechnicalStaff: $acceptedByTechnicalStaff
         approvedByTechnicalStaff: $approvedByTechnicalStaff
         assignedTechnicalStaffId: $assignedTechnicalStaffId
         assignedTechnicalStaffName: $assignedTechnicalStaffName
         awaitingOOP: $awaitingOOP
         awaitingPermitCreation: $awaitingPermitCreation
      ) {
         id
         currentStage
         status
         recordedByReceivingClerk
         approvedByTechnicalStaff
         acceptedByTechnicalStaff
         assignedTechnicalStaffId
         assignedTechnicalStaffName
         awaitingOOP
         awaitingPermitCreation
      }
   }
`;

const UNDO_APPROVE_INVENTORY_REQUEST = gql`
   mutation UndoApproveInventoryRequest(
      $id: ID!
      $currentStage: String!
      $status: String!
      $notes: String
      $approvedByTechnicalStaff: Boolean!
      $ApprovedInventoryRequest: Boolean!
   ) {
      undoApproveInventoryRequest(
         id: $id
         currentStage: $currentStage
         status: $status
         notes: $notes
         approvedByTechnicalStaff: $approvedByTechnicalStaff
         ApprovedInventoryRequest: $ApprovedInventoryRequest
      ) {
         id
         currentStage
         status
         approvedByTechnicalStaff
         ApprovedInventoryRequest
      }
   }
`;

const DELETE_CERTIFICATE = gql`
   mutation DeleteCertificate($id: ID!) {
      deleteCertificate(id: $id) {
         success
         message
      }
   }
`;

const GET_CERTIFICATE = gql`
   query GetCertificatesByApplicationId($applicationId: ID!) {
      getCertificatesByApplicationId(applicationId: $applicationId) {
         id
         certificateNumber
         applicationId
         applicationType
         certificateStatus
         dateCreated
      }
   }
`;

const GET_CERTIFICATES = gql`
   query GetCertificates($status: String) {
      getCertificates(status: $status) {
         id
         certificateNumber
         applicationId
         applicationType
         certificateStatus
         dateCreated
         csawCertificateData {
            registrationType
            ownerName
            address
            chainsawDetails {
               brand
               model
               serialNumber
               dateOfAcquisition
               powerOutput
               maxLengthGuidebar
               countryOfOrigin
               purchasePrice
            }
         }
         covCertificateData {
            applicationNumber
            name
            driverName
            driverAddress
            originAddress
            destinationAddress
            forestProducts {
               kind
               species
               scientificName
               numberOfTrees
               quantity {
                  value
                  unit
               }
               volume {
                  cubicMeters
                  boardFeet
               }
            }
            conveyance
            vehiclePlateNumber
            consignee
            permitNumber
         }
         pltpCertificateData {
            name
            address
            lotNumber
            octNumber
            treeInventory {
               species
               scientificName
               numberOfTreesInventoried
               treeStatus
               grossVolume
               netVolume
            }
            totalTrees
            totalGrossVolume
            totalNetVolume
         }
         pltcpCertificateData {
            name
            occupation
            projectLocation
            projectAddress
            purpose
            treeInventory {
               species
               scientificName
               numberOfTreesInventoried
               grossVolume
               netVolume
               treeCategory
            }
            totalTrees
            totalGrossVolume
            totalNetVolume
         }
      }
   }
`;

const TechnicalStaffDashboard = () => {
   const navigate = useNavigate();
   const location = useLocation();
   const adminId = getAdminId();
   const isAssignedTechnicalStaff = isTechnicalStaff();
   const StaffName = getStaffName();
   const userRoles = getUserRoles();
   const client = useApolloClient();

   // Add mutation hooks
   const [updatePermitStage] = useMutation(UPDATE_PERMIT_STAGE);
   const [undoAcceptanceTechnicalStaff] = useMutation(UNDO_ACCEPTANCE_TECHNICAL_STAFF);
   const [undoApproveInventoryRequest] = useMutation(UNDO_APPROVE_INVENTORY_REQUEST);
   const [deleteCertificate] = useMutation(DELETE_CERTIFICATE);

   const [filters, setFilters] = useState({
      searchTerm: '',
      applicationType: '',
      dateRange: {
         from: undefined,
         to: undefined
      }
   });

   const mainTabs = useMemo(() => ['Applications', 'Awaiting Permit Creation', 'Certificates'], []);
   const subTabs = useMemo(() => ({
      'Applications': ['Pending Reviews', 'Returned Applications', 'Accepted Applications', 'For Inspection and Approval', 'Approved Applications', 'Approved Inventory Request'],
      'Awaiting Permit Creation': ['Awaiting Permit Creation', 'Created Permits'],
      'Certificates': ['Pending Forest Ranger Signature', 'Signed Certificates', 'Pending Release', 'Released Certificates']
   }), []);

   // Initialize tab navigation first
   const {
      activeMainTab,
      activeSubTab,
      handleMainTabChange: handleTabChange,
      handleSubTabChange
   } = useTabNavigation({
      mainTabs,
      subTabs,
      defaultMainTab: getUserRoles().includes('Forest_Ranger') ? 'Certificates' : 'Awaiting Permit Creation',
      defaultSubTab: getUserRoles().includes('Forest_Ranger') ? 'Pending Forest Ranger Signature' : 'Awaiting Permit Creation'
   });

   // Use useMemo for query parameters
   const queryParams = useMemo(() => {
      // Base query params
      let params = {};

      // assigned staff filtering for technical staff
      if (isAssignedTechnicalStaff && adminId) {
         const userRoles = getUserRoles();
         const isForestRanger = userRoles.includes('Forest_Ranger');

         if (activeSubTab === 'Pending Reviews') {
            params.unassignedOnly = true;
         } else if (isForestRanger) {
            // Forest Rangers see COV certificates pending their signature
            params = {
               applicationType: 'Certificate of Verification',
               currentStage: 'PendingForestRangerSignature'
            };
         } else {
            // Regular Technical Staff see their assigned applications
            params.assignedTechnicalStaffId = adminId;
         }
      }

      // existing tab-specific params
      switch (activeSubTab) {
         case 'Pending Reviews':
            return {
               ...params,
               status: 'Submitted',
               currentStage: 'TechnicalStaffReview'
            };
         case 'Returned Applications':
            return {
               ...params,
               currentStage: 'ReturnedByTechnicalStaff',
               status: 'Returned'
            };
         case 'Accepted Applications':
            return {
               ...params,
               acceptedByTechnicalStaff: true
            };
         case 'For Inspection and Approval':
            return {
               ...params,
               currentStage: 'ForInspectionByTechnicalStaff'
            };
         case 'Approved Applications':
            return {
               ...params,
               approvedByTechnicalStaff: true
            };
         case 'Approved Inventory Request':
            return {
               ...params,
               ApprovedInventoryRequest: true
            };
         case 'Awaiting Permit Creation':
            return {
               ...params,
               awaitingPermitCreation: true,
               PermitCreated: false
            };
         case 'Created Permits':
            return {
               ...params,
               PermitCreated: true
            };
         case 'Pending Release':
            return {
               ...params,
               currentStage: 'PendingRelease',
               status: 'In Progress',
               certificateSignedByPENRCENROfficer: true,
               hasCertificate: true,
               PermitCreated: true
            };
         case 'Released Certificates':
            return {
               ...params,
               currentStage: 'Released',
               status: 'Released'
            };
         case 'Pending Forest Ranger Signature':
            return {
               ...params,
               currentStage: 'PendingForestRangerSignature',
               applicationType: 'Certificate of Verification'
            };
         case 'Signed Certificates':
            return {
               ...params,
               currentStage: 'PendingChiefTSDSignature',
               applicationType: 'Certificate of Verification',
               certificateSignedByForestRanger: true,
               certificateSignedByChiefTSD: false,
               status: 'In Progress'
            };

         default:
            return params;
      }
   }, [activeSubTab, adminId, isAssignedTechnicalStaff]);

   // Initialize queries with memoized parameters
   const { applications, loading: appLoading, error: appError, refetch: refetchApps } = useApplications(queryParams);
   const { data: certificatesData, loading: certificatesLoading, error: certificatesError, refetch: refetchCertificates }
      = useQuery(GET_CERTIFICATES, {
         variables: { status: activeSubTab === 'Pending Signature' ? 'Pending Signature' : 'Complete Signatures' },
         skip: !activeMainTab.includes('Certificates'),
      });
   const { applications: allApplications, refetch: refetchAllApplications } = useApplications({});
   const { oops, loading: oopsLoading, error: oopsError, refetch: refetchOOPs } = useOrderOfPayments();

   // Define handleRefetch
   const handleRefetch = useCallback(() => {
      if (activeMainTab === 'Order of Payment') {
         refetchOOPs();
      } else {
         refetchApps();
      }
      // Always refetch all applications for tab counts
      refetchAllApplications();
   }, [activeMainTab, refetchOOPs, refetchApps, refetchAllApplications]);

   useEffect(() => {
      refetchApps();
      refetchAllApplications(); // Also refetch all applications when tab changes
   }, [refetchApps, refetchAllApplications, activeSubTab]);

   useEffect(() => {
      const pollInterval = setInterval(handleRefetch, 30000); // Poll every 30 seconds (increased from 2 seconds)
      return () => clearInterval(pollInterval);
   }, [activeMainTab]);

   // Add manual refresh function
   const handleManualRefresh = useCallback(() => {
      handleRefetch();
   }, [handleRefetch]);

   const filteredApplications = useMemo(() => {
      if (!applications || !Array.isArray(applications)) {
         return [];
      }

      return applications.filter(app => {
         const matchesSearch = app.applicationNumber.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
            app.applicationType.toLowerCase().includes(filters.searchTerm.toLowerCase());

         const matchesType = !filters.applicationType ||
            filters.applicationType === "all" ||
            app.applicationType === filters.applicationType;

         const matchesDateRange = (() => {
            if (!filters.dateRange.from && !filters.dateRange.to) return true;
            const appDate = new Date(app.dateOfSubmission);
            appDate.setHours(0, 0, 0, 0);
            const fromDate = filters.dateRange.from ? new Date(filters.dateRange.from) : null;
            const toDate = filters.dateRange.to ? new Date(filters.dateRange.to) : null;
            if (fromDate) fromDate.setHours(0, 0, 0, 0);
            if (toDate) toDate.setHours(0, 0, 0, 0);
            return (!fromDate || appDate >= fromDate) && (!toDate || appDate <= toDate);
         })();

         return matchesSearch && matchesType && matchesDateRange;
      });
   }, [applications, filters]);

   const filteredOOPs = useMemo(() => {
      if (!oops) return [];

      return oops.filter(oop => {
         // First filter by status based on activeSubTab
         const matchesStatus = (() => {
            if (activeSubTab === 'Pending Approval') {
               return oop.OOPstatus === 'For Approval';
            } else if (activeSubTab === 'Approved OOP') {
               return oop.OOPstatus === 'Awaiting Payment';
            }
            return true;
         })();

         // Then apply search filter
         const matchesSearch = !filters.searchTerm ||
            oop.applicationId.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
            oop.billNo.toLowerCase().includes(filters.searchTerm.toLowerCase());

         // Apply application type filter
         const matchesType = !filters.applicationType ||
            filters.applicationType === "all" ||
            oop.applicationType === filters.applicationType;

         // Apply date range filter
         const matchesDateRange = (() => {
            if (!filters.dateRange.from && !filters.dateRange.to) return true;
            const oopDate = new Date(parseInt(oop.createdAt));
            const fromDate = filters.dateRange.from ? new Date(filters.dateRange.from) : null;
            const toDate = filters.dateRange.to ? new Date(filters.dateRange.to) : null;
            return (!fromDate || oopDate >= fromDate) && (!toDate || oopDate <= toDate);
         })();

         return matchesStatus && matchesSearch && matchesType && matchesDateRange;
      });
   }, [oops, filters, activeSubTab]);

   const getStatusColor = (status) => { // used for mobile
      switch (status.toLowerCase()) {
         case 'submitted':
            return {
               variant: 'default',
               className: 'bg-blue-100 text-blue-800 hover:bg-blue-100/80'
            };
         case 'in progress':
            return {
               variant: 'secondary',
               className: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-100/80'
            };
         case 'returned':
            return {
               variant: 'warning',
               className: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-100/80'
            };
         case 'accepted':
         case 'approved':
            return {
               variant: 'success',
               className: 'bg-green-100 text-green-800 hover:bg-green-100/80'
            };
         case 'released':
            return {
               variant: 'success',
               className: 'bg-green-100 text-green-800 hover:bg-green-100/80'
            };
         default:
            return {
               variant: 'secondary',
               className: 'bg-gray-100 text-gray-800 hover:bg-gray-100/80'
            };
      }
   };

   const isMobile = useMediaQuery('(max-width: 640px)');

   const isChrome = useMemo(() => {
      const userAgent = window.navigator.userAgent.toLowerCase();
      const isBrave = navigator.brave !== undefined;
      return userAgent.includes('chrome') && !userAgent.includes('edg') && !isBrave;
   }, []);

   const [viewMode, setViewMode] = useState(() => {
      return localStorage.getItem('dashboardViewMode') || 'list';
   });

   // Save viewMode to localStorage when it changes
   useEffect(() => {
      localStorage.setItem('dashboardViewMode', viewMode);
   }, [viewMode]);

   // Add these states for modals
   const [selectedApp, setSelectedApp] = useState(null);
   const [isViewModalOpen, setIsViewModalOpen] = useState(false);
   const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
   const [isAuthenticityModalOpen, setIsAuthenticityModalOpen] = useState(false);
   const [isCertificateModalOpen, setIsCertificateModalOpen] = useState(false);
   const [isViewCertModalOpen, setIsViewCertModalOpen] = useState(false);
   const [isInspectionReportsModalOpen, setIsInspectionReportsModalOpen] = useState(false);
   const [isReleaseModalOpen, setIsReleaseModalOpen] = useState(false);
   const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
   const [isForestRangerSignatureModalOpen, setIsForestRangerSignatureModalOpen] = useState(false);

   // Update getActionsForCard function to use the row component's actions
   const getActionsForCard = (app) => {
      return getTSActions(activeSubTab, {
         ...app,
         id: app.id,
         isForestRanger: userRoles.includes('Forest_Ranger')
      }, {
         setIsViewModalOpen: () => {
            setSelectedApp(app);
            setIsViewModalOpen(true);
         },
         setIsReviewModalOpen: () => {
            setSelectedApp(app);
            setIsReviewModalOpen(true);
         },
         setIsAuthenticityModalOpen: () => {
            setSelectedApp(app);
            setIsAuthenticityModalOpen(true);
         },
         setIsCertificateModalOpen: () => {
            setSelectedApp(app);
            setIsCertificateModalOpen(true);
         },
         setIsViewCertModalOpen: () => {
            setSelectedApp(app);
            setIsViewCertModalOpen(true);
         },
         setIsInspectionReportsModalOpen: () => {
            setSelectedApp(app);
            setIsInspectionReportsModalOpen(true);
         },
         setIsReleaseModalOpen: () => {
            setSelectedApp(app);
            setIsReleaseModalOpen(true);
         },
         setIsHistoryModalOpen: () => {
            setSelectedApp(app);
            setIsHistoryModalOpen(true);
         },
         setIsForestRangerSignatureModalOpen: () => {
            setSelectedApp(app);
            setIsForestRangerSignatureModalOpen(true);
         },
         handleUndo: async () => {
            try {
               let variables;

               if (activeSubTab === 'Approved Applications') {
                  variables = {
                     id: app.id,
                     currentStage: 'ForInspectionByTechnicalStaff',
                     status: 'In Progress',
                     notes: 'Approval undone by Technical Staff',
                     approvedByTechnicalStaff: false,
                     awaitingPermitCreation: false
                  };
               } else if (activeSubTab === 'Approved Inventory Request') {
                  try {
                     const { data } = await undoApproveInventoryRequest({
                        variables: {
                           id: app.id,
                           currentStage: 'ForInspectionByTechnicalStaff',
                           status: 'In Progress',
                           notes: 'Inventory request undone by Technical Staff',
                           approvedByTechnicalStaff: false,
                           ApprovedInventoryRequest: false
                        }
                     });

                     if (data?.undoApproveInventoryRequest) {
                        toast.success('Inventory request undone successfully');
                        handleRefetch();
                     } else {
                        toast.error('Failed to undo inventory request');
                     }
                  } catch (error) {
                     console.error('Error undoing inventory request:', error);
                     toast.error(error.message || 'Failed to undo inventory request');
                  }
               } else if (activeSubTab === 'Accepted Applications') {
                  try {
                     const { data } = await undoAcceptanceTechnicalStaff({
                        variables: {
                           id: app.id,
                           currentStage: 'TechnicalStaffReview',
                           status: 'Submitted',
                           acceptedByTechnicalStaff: false,
                           approvedByTechnicalStaff: false,
                           assignedTechnicalStaffId: null,
                           assignedTechnicalStaffName: null,
                           awaitingOOP: false,
                           awaitingPermitCreation: false
                        }
                     });

                     if (data?.undoAcceptanceTechnicalStaff) {
                        toast.success('Application status undone successfully');
                        handleRefetch();
                     } else {
                        toast.error('Failed to undo application status');
                     }
                  } catch (error) {
                     console.error('Error undoing acceptance:', error);
                     toast.error(error.message || 'Failed to undo application status');
                  }
               } else if (activeSubTab === 'Created Permits') {
                  variables = {
                     id: app.id,
                     currentStage: 'AuthenticityApprovedByTechnicalStaff',
                     status: 'In Progress',
                     notes: 'Permit creation undone by Technical Staff',
                     PermitCreated: false,
                     awaitingPermitCreation: true,
                     hasCertificate: false,
                     certificateId: null,
                     certificateSignedByPENRCENROfficer: false
                  };
               } else if (activeSubTab === 'Signed Certificates') {
                  variables = {
                     id: app.id,
                     currentStage: 'PendingForestRangerSignature',
                     status: 'In Progress',
                     certificateSignedByForestRanger: false
                  };
               }

               if (variables) {
                  const { data } = await updatePermitStage({ variables });
                  if (data?.updatePermitStage) {
                     toast.success('Application status undone successfully');
                     handleRefetch();
                  }
               }
            } catch (error) {
               console.error('Error undoing application status:', error);
               toast.error(error.message || 'Failed to undo application status');
            }
         },
         handleDeleteCertificate: async () => {
            try {
               // Get the latest certificate data
               const { data: certData } = await client.query({
                  query: GET_CERTIFICATE,
                  variables: { applicationId: app.id }
               });

               const certificate = certData?.getCertificatesByApplicationId?.[0];
               if (!certificate) {
                  toast.error('No certificate found to delete');
                  return;
               }

               const { data } = await deleteCertificate({
                  variables: { id: certificate.id }
               });

               if (data?.deleteCertificate?.success) {
                  toast.success(data.deleteCertificate.message);
                  // Update permit stage
                  await updatePermitStage({
                     variables: {
                        id: app.id,
                        currentStage: 'AuthenticityApprovedByTechnicalStaff',
                        status: 'In Progress',
                        hasCertificate: false,
                        certificateId: null,
                        awaitingPermitCreation: true,
                        PermitCreated: false
                     }
                  });
                  handleRefetch();
               } else {
                  toast.error('Failed to delete certificate');
               }
            } catch (error) {
               console.error('Error deleting certificate:', error);
               toast.error(error.message || 'Failed to delete certificate');
            }
         }
      });
   };

   const renderApplicationTable = () => {
      // Get the showActionLabels value from the dashboard layout
      const showActionLabels = document.querySelector('[data-show-action-labels]')?.getAttribute('data-show-action-labels') === 'true';

      if (appLoading) {
         return <div className="flex justify-center py-8">Loading applications...</div>;
      }

      if (appError) {
         return <div className="text-destructive text-center py-8">Error loading applications</div>;
      }

      if (!filteredApplications || filteredApplications.length === 0) {
         return (
            <div className="flex flex-col items-center justify-center py-8 space-y-3">
               <div className="p-3 rounded-full bg-gray-100 dark:bg-gray-800">
                  <FileX className="h-12 w-12 text-muted-foreground" />
               </div>
               <h3 className="text-lg font-semibold">No applications found</h3>
               <p className="text-sm text-muted-foreground text-center max-w-sm">
                  {filters.applicationType
                     ? `No ${filters.applicationType} applications found in this section`
                     : activeSubTab === 'Pending Reviews'
                        ? 'No applications pending for review at the moment'
                        : `No applications found in ${activeSubTab}`}
               </p>
            </div>
         );
      }

      if (viewMode === 'grid') {
         return (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 p-4">
               {filteredApplications
                  .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                  .map((app) => (
                     <PersonnelApplicationCard
                        key={app.id}
                        application={{
                           ...app,
                           applicationNumber: app.applicationNumber,
                           applicationType: app.applicationType,
                           dateOfSubmission: app.dateSubmitted || app.dateOfSubmission || app.dateCreated,
                           status: app.status,
                           applicantName: app.applicantName || app.name,
                           currentStage: app.currentStage
                        }}
                        actions={getActionsForCard(app)}
                        getStatusColor={getStatusColor}
                        className="h-full"
                        showActionLabels={showActionLabels}
                     />
                  ))}
            </div>
         );
      }

      if (isMobile) {
         return (
            <div className="space-y-4">
               {filteredApplications.map((app) => (
                  <TS_ApplicationRow
                     key={app.id}
                     app={{...app, isForestRanger: userRoles.includes('Forest_Ranger')}}
                     onReviewComplete={handleRefetch}
                     getStatusColor={getStatusColor}
                     currentTab={activeSubTab}
                     isMobile={true}
                     showActionLabels={showActionLabels}
                  />
               ))}
            </div>
         );
      }

      return (
         <Table>
            {!isMobile && <ApplicationTableHeader />}
            <TableBody>
               {filteredApplications
                  .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                  .map((app, index) => (
                     <TS_ApplicationRow
                        key={app.id}
                        app={{...app, isForestRanger: userRoles.includes('Forest_Ranger')}}
                        onReviewComplete={handleRefetch}
                        getStatusColor={getStatusColor}
                        currentTab={activeSubTab}
                        isMobile={false}
                        isFirstRow={index === 0}
                        showActionLabels={showActionLabels}
                     />
                  ))}
            </TableBody>
         </Table>
      );
   };

   const renderOrderOfPaymentTable = () => {
      if (oopsLoading) return <div className="flex justify-center py-8">Loading order of payments...</div>;
      if (oopsError) return <div className="text-destructive text-center py-8">Error loading order of payments</div>;

      if (!filteredOOPs || filteredOOPs.length === 0) {
         return (
            <div className="text-center py-8">
               <FileX className="mx-auto h-12 w-12 text-muted-foreground" />
               <h3 className="mt-2 text-lg font-semibold">No order of payments found</h3>
               <p className="text-sm text-muted-foreground">
                  {filters.applicationType ?
                     `No order of payments found for ${filters.applicationType}` :
                     'No order of payments available'}
               </p>
            </div>
         );
      }

      return (
         <Table>
            <TableHeader>
               <TableRow>
                  <TableHead className="w-[25%]">Application Number</TableHead>
                  <TableHead className="w-[15%] text-center">Bill Number</TableHead>
                  <TableHead className="w-[15%] text-center">Date</TableHead>
                  <TableHead className="w-[15%] text-center">Amount</TableHead>
                  <TableHead className="w-[15%] text-center">Status</TableHead>
                  <TableHead className="w-[15%] text-center">Actions</TableHead>
               </TableRow>
            </TableHeader>
            <TableBody>
               {filteredOOPs.map((oop) => (
                  <TechnicalStaffOOPRow
                     key={oop._id}
                     oop={oop}
                     onReviewComplete={handleRefetch}
                     currentTab={activeSubTab}
                  />
               ))}
            </TableBody>
         </Table>
      );
   };

   const descriptions = useMemo(() => ({
      // Applications
      'Pending Reviews': 'This is the list of applications pending review to check for completeness and supporting documents.',
      'Returned Applications': 'This is the list of applications that were returned due to incomplete documents or other issues.',
      'Accepted Applications': 'This is the list of applications that have been accepted after review.',
      'For Inspection and Approval': 'This is the list of applications that require physical inspection and authenticity verification.',
      'Approved Applications': 'This is the list of applications that have been approved after inspection.',
      'Approved Inventory Request': 'This is the list of applications that have been approved for inventory request.',

      // Application Awaiting Certificate/Permit Creation
      'Awaiting Permit Creation': 'This is the list of applications waiting for permit/certificate creation after approval.',
      'Created Permits': 'This is the list of applications with created permits/certificates.',

      // Certificates/Permits
      'Pending Forest Ranger Signature': 'This is the list of COV certificates waiting for Forest Ranger signature.',
      'Signed Certificates': 'This is the list of COV certificates that you have signed.',
      'Pending Release': 'This is the list of permits/certificates that are ready for release to the applicant.',
      'Released Certificates': 'This is the list of permits/certificates that have been released to the applicant.',

      // Order of Payment
      'Pending Approval': 'This is the list of Order of Payments pending for your approval.',
      'Approved OOP': 'This is the list of Order of Payments that you have approved.'
   }), []);

   const descriptionText = useTypewriter(descriptions[activeSubTab] || '');

   const renderTabDescription = () => (
      <div className="mb-4 -mt-4">
         <h1 className="text-sm min-h-[20px] text-black dark:text-gray-300">{descriptionText}</h1>
      </div>
   );

   // function to get count for each tab
   const getTabCounts = useMemo(() => {
      if (!allApplications || !oops) return {};

      const counts = {};

      // Calculate counts for all tabs regardless of active tab
      // Applications
      counts['Pending Reviews'] = allApplications.filter(app =>
         !app.assignedTechnicalStaffId &&
         app.status === 'Submitted' &&
         app.currentStage === 'TechnicalStaffReview'
      ).length;

      counts['Returned Applications'] = allApplications.filter(app =>
         app.assignedTechnicalStaffId === adminId &&
         app.currentStage === 'ReturnedByTechnicalStaff' &&
         app.status === 'Returned'
      ).length;

      counts['Accepted Applications'] = allApplications.filter(app =>
         app.assignedTechnicalStaffId === adminId &&
         app.acceptedByTechnicalStaff === true
      ).length;

      counts['For Inspection and Approval'] = allApplications.filter(app =>
         app.assignedTechnicalStaffId === adminId &&
         app.currentStage === 'ForInspectionByTechnicalStaff'
      ).length;

      counts['Approved Applications'] = allApplications.filter(app =>
         app.assignedTechnicalStaffId === adminId &&
         app.approvedByTechnicalStaff === true
      ).length;

      counts['Approved Inventory Request'] = allApplications.filter(app =>
         app.assignedTechnicalStaffId === adminId &&
         app.ApprovedInventoryRequest === true
      ).length;

      // Debug log for Awaiting Permit Creation filtering
      const awaitingPermitCreationApps = allApplications.filter(app =>
         app.assignedTechnicalStaffId === adminId &&
         app.awaitingPermitCreation === true &&
         !app.PermitCreated
      );

      counts['Awaiting Permit Creation'] = awaitingPermitCreationApps.length;

      counts['Created Permits'] = allApplications.filter(app =>
         app.assignedTechnicalStaffId === adminId &&
         app.PermitCreated === true
      ).length;

      // Certificates/Permits tab counts
      counts['Pending Release'] = allApplications.filter(app =>
         app.assignedTechnicalStaffId === adminId &&
         app.currentStage === 'PendingRelease' &&
         app.certificateSignedByPENRCENROfficer === true &&
         app.PermitCreated === true
      ).length;

      counts['Released Certificates'] = allApplications.filter(app =>
         app.assignedTechnicalStaffId === adminId &&
         app.currentStage === 'Released' &&
         app.status === 'Released'
      ).length;

      // console.log('Final Tab Counts:', counts);
      return counts;
   }, [allApplications, adminId]);

   // useEffect(() => {
   //    console.log('All Applications:', allApplications?.map(app => ({
   //       id: app.id,
   //       currentStage: app.currentStage,
   //       awaitingPermitCreation: app.awaitingPermitCreation,
   //       PermitCreated: app.PermitCreated,
   //       assignedTechnicalStaffId: app.assignedTechnicalStaffId
   //    })));
   // }, [allApplications]);

   // Keyboard navigation
   useEffect(() => {
      const handleKeyPress = (e) => {
         if (e.altKey) {
            switch (e.key) {
               case 'ArrowRight':
               case 'ArrowLeft':
               case 'ArrowUp':
               case 'ArrowDown':
                  // Prevent default browser navigation
                  e.preventDefault();

                  if (e.key === 'ArrowRight') {
                     const currentMainIndex = mainTabs.indexOf(activeMainTab);
                     if (currentMainIndex < mainTabs.length - 1) {
                        handleTabChange(mainTabs[currentMainIndex + 1]);
                     }
                  } else if (e.key === 'ArrowLeft') {
                     const prevMainIndex = mainTabs.indexOf(activeMainTab);
                     if (prevMainIndex > 0) {
                        handleTabChange(mainTabs[prevMainIndex - 1]);
                     }
                  } else if (e.key === 'ArrowDown') {
                     const currentSubTabs = subTabs[activeMainTab];
                     const currentSubIndex = currentSubTabs.indexOf(activeSubTab);
                     if (currentSubIndex < currentSubTabs.length - 1) {
                        handleSubTabChange(currentSubTabs[currentSubIndex + 1]);
                     }
                  } else if (e.key === 'ArrowUp') {
                     const currentSubTabsList = subTabs[activeMainTab];
                     const prevSubIndex = currentSubTabsList.indexOf(activeSubTab);
                     if (prevSubIndex > 0) {
                        handleSubTabChange(currentSubTabsList[prevSubIndex - 1]);
                     }
                  }
                  break;
            }
         }
      };

      window.addEventListener('keydown', handleKeyPress);
      return () => window.removeEventListener('keydown', handleKeyPress);
   }, [activeMainTab, activeSubTab, mainTabs, subTabs, handleTabChange, handleSubTabChange]);

   // Update URL when tabs change - ensure we only update query params
   useEffect(() => {
      const params = new URLSearchParams(location.search);
      params.set('tab', activeMainTab);
      params.set('subtab', activeSubTab);
      // Use replace to update only the search params without changing the path
      navigate({
         pathname: location.pathname, // Keep the current path
         search: params.toString()
      }, { replace: true });
   }, [activeMainTab, activeSubTab, navigate, location.pathname]);

   const { startTour: initiateTour } = useTourGuide({
      tourId: 'technical-staff-tour',
      onFinish: () => {
         console.log('Tour finished');
      },
   });

   const technicalStaffTourSteps = useMemo(() => {
      // Base steps that are always available
      const baseSteps = [
         {
            target: '.main-tabs',
            content: 'Switch between Applications, Awaiting Permit Creation, and Certificates/Permits using these main tabs',
            placement: 'bottom',
            disableBeacon: true,
         },
         {
            target: '.sub-tabs',
            content: 'View different application statuses based on their current stage',
            placement: 'bottom',
         },
         {
            target: '.applications-filters',
            content: 'Filter applications by type, status, and date range',
            placement: 'bottom',
         }
      ];

      // Add table step based on whether we have data or not
      if (filteredApplications?.length > 0) {
         baseSteps.push(
            {
               target: '.applications-table',
               content: 'View all applications assigned to you and their details in this table',
               placement: 'top',
            },
            {
               target: '.table-actions',
               content: 'Here you can find different actions for each application',
               placement: 'left',
            },
            {
               target: '.view-action',
               content: 'Click here to view the complete details of the application',
               placement: 'left',
            },
            {
               target: '.history-action',
               content: 'Click here to view the application history',
               placement: 'left',
            },
            {
               target: '.review-action',
               content: 'Click here to review and process new applications',
               placement: 'left',
            },
            {
               target: '.authenticity-action',
               content: 'Click here to verify and approve application authenticity',
               placement: 'left',
            },
            {
               target: '.generate-cert-action',
               content: 'Click here to generate certificates for approved applications',
               placement: 'left',
            },
            {
               target: '.view-cert-action',
               content: 'Click here to view generated certificates',
               placement: 'left',
            },
            {
               target: '.undo-action',
               content: 'Click here to undo previous actions if needed',
               placement: 'left',
            }
         );
      } else {
         baseSteps.push({
            target: '.applications-table',
            content: 'Applications will appear here once they are assigned to you',
            placement: 'top',
         });
      }

      return baseSteps;
   }, [filteredApplications?.length]);

   // Add useEffect for tour initialization
   useEffect(() => {
      const tourButton = document.querySelector('.help-tour-button');
      if (tourButton) {
         tourButton.disabled = appLoading || certificatesLoading || oopsLoading;
      }
   }, [appLoading, certificatesLoading, oopsLoading]);

   const handleStartTour = useCallback(async () => {
      // Wait for data to be loaded
      if (appLoading || certificatesLoading || oopsLoading) {
         toast.info('Please wait while data is loading...');
         return;
      }

      // Start the tour
      initiateTour(technicalStaffTourSteps);
   }, [appLoading, certificatesLoading, oopsLoading, initiateTour, technicalStaffTourSteps]);

   // Add this useEffect to debug
   useEffect(() => {
      // Set up a MutationObserver to watch for changes to data-show-action-labels
      const targetNode = document.body;
      const config = { attributes: true, childList: true, subtree: true };

      const callback = (mutationsList) => {
         for (const mutation of mutationsList) {
            if (mutation.type === 'attributes' &&
               mutation.attributeName === 'data-show-action-labels') {
               const showLabels = document.querySelector('[data-show-action-labels]')?.getAttribute('data-show-action-labels') === 'true';
               console.log('Action labels setting changed:', showLabels);
               // Force re-render when the setting changes
               forceUpdate();
            }
         }
      };

      const observer = new MutationObserver(callback);
      observer.observe(targetNode, config);

      return () => observer.disconnect();
   }, []);

   // Use this to force re-render
   const [, updateState] = useState({});
   const forceUpdate = () => updateState({});

   const [currentPage, setCurrentPage] = useState(1);
   const [itemsPerPage, setItemsPerPage] = useState(10);
   const [totalItems, setTotalItems] = useState(0);

   useEffect(() => {
      setTotalItems(filteredApplications.length);
   }, [filteredApplications]);

   return (
      <DashboardLayout
         title={`Technical Staff Dashboard - ${StaffName} (${getStaffRole()})`} // commented out for pre-deployment preparation
         // title='Technical Staff Dashboard'
         description="Manage and process permit applications"
         onRefresh={handleRefetch}
         isMobile={isMobile}
         mainTabs={mainTabs}
         subTabs={subTabs}
         activeMainTab={activeMainTab}
         activeSubTab={activeSubTab}
         onMainTabChange={handleTabChange}
         onSubTabChange={handleSubTabChange}
         tabDescription={renderTabDescription()}
         filters={<ApplicationFilters filters={filters} setFilters={setFilters} />}
         tabCounts={getTabCounts}
         helpTour={handleStartTour}
         viewMode={viewMode}
         onViewModeChange={() => setViewMode(prev => prev === 'list' ? 'grid' : 'list')}
         currentPage={currentPage}
         totalItems={totalItems}
         itemsPerPage={itemsPerPage}
         onPageChange={setCurrentPage}
         onItemsPerPageChange={setItemsPerPage}
      >
         {activeMainTab === 'Order of Payment' ?
            renderOrderOfPaymentTable() :
            renderApplicationTable()
         }

         {/* Add all modals */}
         <ApplicationViewModal
            isOpen={isViewModalOpen}
            onClose={() => setIsViewModalOpen(false)}
            application={selectedApp}
         />
         <TS_ReviewModal
            isOpen={isReviewModalOpen}
            onClose={() => setIsReviewModalOpen(false)}
            application={selectedApp}
            onReviewComplete={handleRefetch}
         />
         <TS_AuthenticityReviewModal
            isOpen={isAuthenticityModalOpen}
            onClose={() => setIsAuthenticityModalOpen(false)}
            application={selectedApp}
            onReviewComplete={handleRefetch}
         />
         <CertificateActionHandler
            isOpen={isCertificateModalOpen}
            onClose={() => setIsCertificateModalOpen(false)}
            application={selectedApp}
            onComplete={handleRefetch}
         />
         <CertificateViewModal
            isOpen={isViewCertModalOpen}
            onClose={() => setIsViewCertModalOpen(false)}
            certificate={selectedApp?.certificate}
         />
         <TS_InspectionReportsViewModal
            isOpen={isInspectionReportsModalOpen}
            onClose={() => setIsInspectionReportsModalOpen(false)}
            permitId={selectedApp?.id || ''}
         />
         <ReleaseConfirmationModal
            isOpen={isReleaseModalOpen}
            onClose={() => setIsReleaseModalOpen(false)}
            application={selectedApp}
            onComplete={handleRefetch}
         />
         <HistoryModal
            isOpen={isHistoryModalOpen}
            onClose={() => setIsHistoryModalOpen(false)}
            history={selectedApp?.history}
         />
         <ForestRangerSignatureModal
            isOpen={isForestRangerSignatureModalOpen}
            onClose={() => setIsForestRangerSignatureModalOpen(false)}
            application={selectedApp}
            onSignComplete={handleRefetch}
         />
      </DashboardLayout>
   );
};

export default TechnicalStaffDashboard;